import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { HiPlus, HiX } from "react-icons/hi";
import { twMerge } from "tailwind-merge";
import { CustomButton } from "../../../atoms/CustomButton";
import Spinner from "../../../atoms/Spinner";
import ConfirmModal from "../../../public/ConfirmModal";
import NoTranslate from "../../../atoms/NoTranslate";
import TimeZoneSelector from "../entities/TimeZoneSelector";
import useAuth from "../../../../hooks/useAuth";
import {
  MemberSettings,
  TimeRange,
  WEEKDAY_KEYS,
  WeekdayKey,
  useDeleteMemberSettings,
  useGetMemberSettings,
  useUpdateMemberSettings,
} from "../../../../hooks/agenda";
import {
  MIDNIGHT,
  browserTimeZone,
  capitalizeFirst,
  weekdayKeyIndex,
} from "./agendaDates";
import { useAgendaFailure } from "./agendaTypes";

type Hours = Record<WeekdayKey, TimeRange[]>;

const DEFAULT_RANGE: TimeRange = ["09:00", "17:00"];

const emptyHours = (): Hours =>
  WEEKDAY_KEYS.reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {} as Hours);

const defaultHours = (): Hours => {
  const hours = emptyHours();
  (["mon", "tue", "wed", "thu", "fri"] as WeekdayKey[]).forEach((key) => {
    hours[key] = [[...DEFAULT_RANGE]];
  });
  return hours;
};

const normalizeHours = (settings: MemberSettings): Hours => {
  const hours = emptyHours();
  WEEKDAY_KEYS.forEach((key) => {
    hours[key] = (settings.hours?.[key] ?? []).map(
      ([start, end]) => [start, end] as TimeRange
    );
  });
  return hours;
};

type DayError = "order" | "overlap" | "quarter" | null;

const QUARTER_TIME = /^([01]\d|2[0-3]):(00|15|30|45)$/;
const DAY_MINUTES = 24 * 60;

export const dayError = (ranges: TimeRange[]): DayError => {
  if (ranges.some(([start, end]) => !start || !end || start >= end))
    return "order";
  if (
    ranges.some(
      ([start, end]) =>
        !QUARTER_TIME.test(start) ||
        !(QUARTER_TIME.test(end) || end === MIDNIGHT)
    )
  )
    return "quarter";
  const sorted = [...ranges].sort((a, b) => a[0].localeCompare(b[0]));
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i][0] < sorted[i - 1][1]) return "overlap";
  }
  return null;
};

const toMinutes = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const toTime = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
    minutes % 60
  ).padStart(2, "0")}`;

/** A time field cannot hold 24:00: midnight shows as 00:00, which can only mean midnight at the end of a range. */
export const endToInput = (end: string) => (end === MIDNIGHT ? "00:00" : end);
export const endFromInput = (value: string) =>
  value === "00:00" ? MIDNIGHT : value;

/**
 * The range a click on « add » proposes: up to two hours right after the last one entered, else in the first free
 * stretch of the day; null once the day is full.
 */
const MAX_RANGES_PER_DAY = 8;

export const canAddRange = (ranges: TimeRange[]): boolean =>
  ranges.length < MAX_RANGES_PER_DAY && nextFreeRange(ranges) !== null;

export const nextFreeRange = (ranges: TimeRange[]): TimeRange | null => {
  if (ranges.length === 0) return [...DEFAULT_RANGE];
  const taken = ranges
    .map(([start, end]) => [toMinutes(start), toMinutes(end)])
    .filter(([start, end]) => !Number.isNaN(start) && !Number.isNaN(end))
    .sort((a, b) => a[0] - b[0]);
  const gaps: [number, number][] = [];
  let cursor = 0;
  taken.forEach(([start, end]) => {
    if (start > cursor) gaps.push([cursor, start]);
    cursor = Math.max(cursor, end);
  });
  if (cursor < DAY_MINUTES) gaps.push([cursor, DAY_MINUTES]);
  const after = toMinutes(ranges[ranges.length - 1][1]);
  const gap = gaps.find(([start]) => start === after) ?? gaps[0];
  if (!gap) return null;
  return [toTime(gap[0]), toTime(Math.min(gap[0] + 120, gap[1]))];
};

const TIME_FIELD =
  "block w-full min-w-0 rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 md:py-2 md:text-sm";

interface UseMemberHoursOptions {
  userId: string;
  open: boolean;
  canEdit: boolean;
  onSaved: (settings: MemberSettings) => void;
  onRemoved: () => void;
}

/** One person's weekly hours, loaded when `open`, saved or removed through the agenda's member settings. */
export const useMemberHours = ({
  userId,
  open,
  canEdit,
  onSaved,
  onRemoved,
}: UseMemberHoursOptions) => {
  const { t } = useTranslation();
  const failure = useAgendaFailure();
  const { user, customer_identification: customerId } = useAuth();
  const entityId = user.entity_identification;

  const { data, isLoading, isError, error } = useGetMemberSettings(
    customerId,
    entityId,
    userId,
    { enabled: open }
  );
  const save = useUpdateMemberSettings(customerId, entityId);
  const remove = useDeleteMemberSettings(customerId, entityId);

  const [timezone, setTimezone] = useState<string>(browserTimeZone());
  const [hours, setHours] = useState<Hours>(emptyHours);

  const hasSettings = Boolean(data);

  useEffect(() => {
    if (!open || isLoading) return;
    setTimezone(data?.tz || browserTimeZone());
    setHours(data ? normalizeHours(data) : defaultHours());
  }, [open, isLoading, data]);

  const errors = useMemo(
    () =>
      WEEKDAY_KEYS.reduce((acc, key) => {
        acc[key] = dayError(hours[key]);
        return acc;
      }, {} as Record<WeekdayKey, DayError>),
    [hours]
  );
  const hasErrors = Object.values(errors).some(Boolean);

  const updateRange = (
    key: WeekdayKey,
    index: number,
    position: 0 | 1,
    value: string
  ) =>
    setHours((prev) => ({
      ...prev,
      [key]: prev[key].map((range, i) =>
        i === index
          ? ((position === 0
              ? [value, range[1]]
              : [range[0], value]) as TimeRange)
          : range
      ),
    }));

  const addRange = (key: WeekdayKey) =>
    setHours((prev) => {
      const next = nextFreeRange(prev[key]);
      return next ? { ...prev, [key]: [...prev[key], next] } : prev;
    });

  const removeRange = (key: WeekdayKey, index: number) =>
    setHours((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));

  const loadFailed = isError && error?.response?.status !== 404;

  const onSave = () => {
    if (!canEdit || hasErrors || loadFailed) return;
    const sorted = WEEKDAY_KEYS.reduce((acc, key) => {
      acc[key] = [...hours[key]].sort((a, b) => a[0].localeCompare(b[0]));
      return acc;
    }, {} as Hours);
    const settings: MemberSettings = { tz: timezone, hours: sorted };
    save.mutate(
      { userId, settings },
      {
        onSuccess: () => {
          toast.success(t("agenda.settings.saved") as string);
          onSaved(settings);
        },
        onError: (err) => {
          toast.error(failure(err, "agenda.settings.saveError"));
        },
      }
    );
  };

  const onRemove = (onSettled: () => void) => {
    if (!canEdit || !hasSettings) return;
    remove.mutate(userId, {
      onSuccess: () => {
        toast.success(t("agenda.settings.removed") as string);
        onRemoved();
      },
      onError: (err) => {
        toast.error(failure(err, "agenda.settings.removeError"));
      },
      onSettled,
    });
  };

  return {
    canEdit,
    isSelf: userId === user.user_identification,
    isLoading,
    loadFailed,
    hasSettings,
    timezone,
    setTimezone,
    hours,
    errors,
    hasErrors,
    updateRange,
    addRange,
    removeRange,
    onSave,
    isSaving: save.isLoading,
    onRemove,
    isRemoving: remove.isLoading,
  };
};

export type MemberHours = ReturnType<typeof useMemberHours>;

export const MemberHoursFields: React.FC<{
  form: MemberHours;
  personName?: string;
}> = ({ form, personName }) => {
  const { t } = useTranslation();
  const { canEdit, hours, errors } = form;
  const someoneElse = !form.isSelf && personName ? personName : null;

  const weekdayLabel = (key: WeekdayKey) =>
    capitalizeFirst(
      moment.localeData().weekdays(moment().day(weekdayKeyIndex(key)))
    );

  if (form.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="md" />
      </div>
    );
  }
  if (form.loadFailed) {
    return (
      <p className="py-6 text-sm text-red-600" role="alert">
        {t("agenda.settings.loadError")}
      </p>
    );
  }

  return (
    <div className="space-y-6 pb-4">
      <p className="text-sm text-gray-500">
        {someoneElse ? (
          <NoTranslate>
            {t("agenda.settings.descriptionFor", { name: someoneElse })}
          </NoTranslate>
        ) : (
          t("agenda.settings.description")
        )}
      </p>
      {!form.hasSettings && (
        <p className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">
          {someoneElse ? (
            <NoTranslate>
              {t("agenda.settings.noneNoteFor", { name: someoneElse })}
            </NoTranslate>
          ) : (
            t("agenda.settings.noneNote")
          )}
        </p>
      )}

      <div className="space-y-1">
        <label
          htmlFor="agenda-member-timezone"
          className="block text-sm font-medium text-gray-700"
        >
          {t("agenda.settings.timezoneLabel")}
        </label>
        {canEdit ? (
          <TimeZoneSelector
            inputId="agenda-member-timezone"
            currentTimeZone={form.timezone}
            onChange={form.setTimezone}
          />
        ) : (
          <p className="text-sm text-gray-900">{form.timezone}</p>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900">
          {t("agenda.settings.hoursTitle")}
        </h3>
        {!form.hasSettings && canEdit && (
          <p className="mt-0.5 text-xs text-gray-500">
            {t("agenda.settings.suggestedHours")}
          </p>
        )}
        <div className="mt-2 divide-y divide-gray-100">
          {WEEKDAY_KEYS.map((key) => {
            const ranges = hours[key];
            const error = errors[key];
            return (
              <div
                key={key}
                className="grid grid-cols-1 gap-2 py-3 sm:grid-cols-[7.5rem_1fr] sm:gap-3"
              >
                <div className="pt-2 text-sm font-medium text-gray-700">
                  {weekdayLabel(key)}
                </div>
                <div className="min-w-0 space-y-2">
                  {ranges.length === 0 && (
                    <p className="pt-2 text-sm text-gray-500">
                      {t("agenda.settings.closed")}
                    </p>
                  )}
                  {ranges.map(([start, end], index) => (
                    <React.Fragment key={index}>
                      <div className="flex min-w-0 items-center gap-2">
                        <input
                          type="time"
                          step={900}
                          value={start}
                          disabled={!canEdit}
                          aria-label={
                            t("agenda.settings.rangeStart", {
                              day: weekdayLabel(key),
                            }) as string
                          }
                          onChange={(e) =>
                            form.updateRange(key, index, 0, e.target.value)
                          }
                          className={twMerge(
                            TIME_FIELD,
                            error && "border-red-500"
                          )}
                        />
                        <span className="shrink-0 text-sm text-gray-500">
                          {t("agenda.settings.rangeTo")}
                        </span>
                        <input
                          type="time"
                          step={900}
                          value={endToInput(end)}
                          disabled={!canEdit}
                          aria-label={
                            t("agenda.settings.rangeEnd", {
                              day: weekdayLabel(key),
                            }) as string
                          }
                          aria-describedby={
                            end === MIDNIGHT
                              ? `agenda-midnight-${key}-${index}`
                              : undefined
                          }
                          onChange={(e) =>
                            form.updateRange(
                              key,
                              index,
                              1,
                              endFromInput(e.target.value)
                            )
                          }
                          className={twMerge(
                            TIME_FIELD,
                            error && "border-red-500"
                          )}
                        />
                        {canEdit && (
                          <CustomButton
                            type="button"
                            variant="none"
                            size="icon"
                            onClick={() => form.removeRange(key, index)}
                            className="shrink-0 rounded-md text-gray-400 enabled:hover:text-red-600"
                            icon={<HiX size={16} />}
                            aria-label={
                              t("agenda.settings.removeRange") as string
                            }
                          />
                        )}
                      </div>
                      {end === MIDNIGHT && (
                        <p
                          id={`agenda-midnight-${key}-${index}`}
                          className="text-xs text-gray-500"
                        >
                          {t("agenda.settings.untilMidnight")}
                        </p>
                      )}
                    </React.Fragment>
                  ))}
                  {error && (
                    <p className="text-xs text-red-600" role="alert">
                      {t(`agenda.settings.errors.${error}`)}
                    </p>
                  )}
                  {canEdit && canAddRange(ranges) && (
                    <CustomButton
                      variant="none"
                      size="xs"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      comp={
                        <>
                          <HiPlus size={14} />
                          {t("agenda.settings.addRange")}
                        </>
                      }
                      onClick={() => form.addRange(key)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const MemberHoursFooter: React.FC<{
  form: MemberHours;
  onCancel: () => void;
  personName?: string;
}> = ({ form, onCancel, personName }) => {
  const { t } = useTranslation();
  const [confirmRemove, setConfirmRemove] = useState(false);
  if (!form.canEdit) return null;
  const busy = form.isSaving || form.isRemoving;
  const someoneElse = !form.isSelf && personName ? personName : null;
  return (
    <div className="sticky bottom-0 left-0 right-0 -mx-4 -mb-4 border-t border-gray-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {form.hasSettings && !form.isLoading && (
          <CustomButton
            variant="secondary"
            icon="trash"
            className="mr-auto text-red-600"
            title={t("agenda.settings.remove") as string}
            onClick={() => setConfirmRemove(true)}
            disabled={busy}
          />
        )}
        <CustomButton
          variant="secondary"
          title={t("common.cancel") as string}
          onClick={onCancel}
          disabled={busy}
        />
        <CustomButton
          variant="primary"
          title={t("agenda.settings.save") as string}
          onClick={form.onSave}
          isLoading={form.isSaving}
          disabled={busy || form.hasErrors || form.isLoading || form.loadFailed}
        />
      </div>
      <ConfirmModal
        show={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        title={t("agenda.settings.removeTitle")}
        description={
          someoneElse ? (
            <NoTranslate>
              {t("agenda.settings.removeDescriptionFor", {
                name: someoneElse,
              })}
            </NoTranslate>
          ) : (
            t("agenda.settings.removeDescription")
          )
        }
        cancelBtnName={t("common.cancel")}
        cancelBtnFn={() => setConfirmRemove(false)}
        confirmBtnName={t("agenda.settings.removeConfirm")}
        confirmBtnFn={() => form.onRemove(() => setConfirmRemove(false))}
        ConfirmBtnColor="red"
        isLoading={form.isRemoving}
      />
    </div>
  );
};
